import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from './auth.service';
import { AuthResult } from './auth.result';
import { SpecialistsService } from '@common/services/specialist/specialists.service';
import { LoggingService } from '@common/services/utility/logging.service';
import { NavigationService } from '@common/services/utility/navigation.service';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
})
export class AuthComponent implements OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private loggingService: LoggingService,
    private navigationService: NavigationService,
    private readonly specialistsService: SpecialistsService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (await this.isUrlHasErrorParam()) {
      return;
    }

    if (this.isDontHasCodeOrAuthState()) {
      return;
    }

    if (await this.isInvalidState()) {
      return;
    }

    if (!(await this.tryAuthSpecialist())) {
      return;
    }

    const specialist = this.specialistsService.specialist;
    this.loggingService.info(`User ${specialist.username}, IsAdmin: ${specialist.isAdmin} logged in`);

    this.navigationService.navigateToMainForUserPage(specialist);
  }

  private async navigateToErrorPage(errorMessage: string): Promise<void> {
    await this.loggingService.warning(
      { message: errorMessage, name: 'Signin Error' },
      null,
      'navigateToErrorPage',
      this.constructor.name,
    );

    this.navigationService.navigateToErrorPage({
      title: 'Signin Error',
      description: `Internal server error. Please contact system administrator. Details: ${errorMessage}`,
    });
  }

  private async isUrlHasErrorParam(): Promise<boolean> {
    const error = this.activatedRoute.snapshot.queryParamMap.get('error');
    const errorDesc = this.activatedRoute.snapshot.queryParamMap.get('error_description');
    if (error) {
      const errorMessage = `Error of type '${error}' occurred while redirecting to cognito sign in page. Error description: ${errorDesc}`;
      this.loggingService.error(
        { message: errorMessage, name: 'Signin Error' },
        null,
        'isUrlHasErrorParam',
        this.constructor.name,
      );
      await this.navigateToErrorPage(errorMessage);
      return true;
    }
    return false;
  }

  private isDontHasCodeOrAuthState(): boolean {
    if (!this.activatedRoute.snapshot.queryParamMap.has('code') || !this.authService.authState) {
      this.authService.redirectToAuthPage();
      return true;
    }
    return false;
  }

  private async isInvalidState(): Promise<boolean> {
    const state = this.activatedRoute.snapshot.queryParamMap.get('state');
    if (state !== this.authService.authState) {
      const errorMessage = `Invalid state has been received. Actual: ${state}, expected: ${this.authService.authState}`;
      this.loggingService.error(
        { message: errorMessage, name: 'Signin Error' },
        null,
        'isInvalidState',
        this.constructor.name,
      );
      await this.navigateToErrorPage(errorMessage);
      return true;
    }
    return false;
  }

  private async tryAuthSpecialist(): Promise<boolean> {
    const authCode = this.activatedRoute.snapshot.queryParamMap.get('code');
    const authResult = await this.authService.signIn(authCode);
    if (authResult !== AuthResult.Success) {
      const errorMessage = 'Error while getting authorization tokens.';
      this.loggingService.error({ message: errorMessage, name: 'Signin Error' });
      this.navigateToErrorPage(errorMessage);
      return false;
    }
    return true;
  }
}
